import type { Optional, PropsWithChildren } from '@shared/utils';
import { createContext, useMemo, useState } from 'react';

interface QuestionChangeInformationValue {
  topicId: Optional<string>;
  subTopicId: Optional<string>;
  questionGroupSize: Optional<number>;
}

interface QuestionChangeInformationHandler {
  changeTopicId: (topicId: string | undefined) => void;
  changeSubTopicId: (subTopicId: string | undefined) => void;
  handleSetQBankStructureId: (qBankStructureId: string | null) => void;
  handleInformationLoaded: () => void;
}

interface QuestionChangeInformationProviderProps extends PropsWithChildren {
  initialValues?: {
    questionGroupSize: number;
  };
}

type QuestionChangeInformationContextData = QuestionChangeInformationValue &
  QuestionChangeInformationHandler & {
    qBankStructureId: string | null;
    isInformationLoading: boolean;
  };

const DEFAULT_QUESTION_CHANGE_INFORMATION: QuestionChangeInformationValue = {
  topicId: undefined,
  subTopicId: undefined,
  questionGroupSize: undefined,
};

export const QuestionChangeInformationContext = createContext<
  QuestionChangeInformationContextData | undefined
>(undefined);

export function QuestionChangeInformationProvider(
  props: Readonly<QuestionChangeInformationProviderProps>,
) {
  const { children, initialValues } = props;

  const [questionChangeInformation, setQuestionChangeInformation] =
    useState<QuestionChangeInformationValue>({
      ...DEFAULT_QUESTION_CHANGE_INFORMATION,
      questionGroupSize: initialValues?.questionGroupSize,
    });

  const [qBankStructureId, setQBankStructureId] = useState<string | null>(null);
  const [isInformationLoading, setIsInformationLoading] = useState(true);

  const changeTopicId = (topicId: string | undefined) => {
    setQuestionChangeInformation((prev) => ({
      ...prev,
      topicId,
      subTopicId: undefined,
    }));
  };

  const changeSubTopicId = (subTopicId: string | undefined) => {
    setQuestionChangeInformation((prev) => ({
      ...prev,
      subTopicId,
    }));
  };

  const handleSetQBankStructureId = (qBankStructureId: string | null) => {
    setQBankStructureId(qBankStructureId);
  };

  const handleInformationLoaded = () => {
    setIsInformationLoading(false);
  };

  const contextValue = useMemo(
    () => ({
      ...questionChangeInformation,
      qBankStructureId,
      isInformationLoading,
      changeTopicId,
      changeSubTopicId,
      handleSetQBankStructureId,
      handleInformationLoaded,
    }),
    [questionChangeInformation],
  );

  return (
    <QuestionChangeInformationContext.Provider value={contextValue}>
      {children}
    </QuestionChangeInformationContext.Provider>
  );
}
